<template>
    <v-card
        outlined
        class="details-card mb-2"
        data-test-id="box-spending">
        <v-card-title class="details-title text-body-2 d-flex justify-space-between text-uppercase">
            {{ $t('spending') }}
        </v-card-title>
        <v-card-text>
            <div v-if="spendingDataUnAvailable">
                <v-row class="pb-5 pl-4 mt-8 text-h6">
                    {{ $t('serviceDetailsTab.spendNotAvailable') }}
                </v-row>
            </div>
            <div
                v-else-if="loading"
                class="d-flex justify-center">
                <v-progress-circular
                    indeterminate
                    size="60" />
            </div>
            <div v-else>
                <v-row class="text-caption pb-5">
                    <v-col
                        cols="5"
                        class="px-3 py-0">
                        {{ $t('serviceDetailsTab.currentCharges') }}
                    </v-col>
                    <v-col
                        cols="7"
                        class="px-3 py-0 text-body-1">
                        {{ $t('currency', [totalCost.toFixed(2)]) }}
                    </v-col>
                </v-row>
                <template v-if="isServerless">
                    <template v-if="shouldShowDetailedServerlessSpending">
                        <v-row
                            v-for="component in components"
                            :key="component.name"
                            class="text-caption">
                            <v-col
                                cols="5"
                                class="px-3 py-0">
                                <template v-if="component.name?.toLowerCase()?.includes('scu')">
                                    {{ $t('serviceDetailsTab.scuHours') }} ({{ component.quantity?.toFixed(2) }})
                                    <ScuHelpTooltip />
                                </template>
                                <template v-else>
                                    <span
                                        class="text-capitalize"
                                        :class="{ 'text-uppercase': component.name === 'iops' }">
                                        {{ component.name }}
                                    </span> ({{ component.quantity }} {{ component.usageUnit }})
                                </template>
                            </v-col>
                            <v-col
                                cols="7"
                                class="px-3 py-0 text-body-1">
                                {{ $t('currency', [component.estimate.toFixed(2)]) }}
                                <span
                                    v-if="component.name === 'scu'"
                                    class="d-inline-flex">
                                    <HelpToolTip
                                        :size="16">
                                        <template #message>
                                            <p>
                                                {{ $t('billings.serverlessComputeTotalTip') }}
                                            </p>
                                        </template>
                                    </HelpToolTip>
                                </span>
                            </v-col>
                        </v-row>
                    </template>
                    <v-row
                        v-else
                        class="text-caption pb-5">
                        <v-col
                            cols="5"
                            class="px-3 py-0">
                            {{ $t('serviceDetailsTab.scuHours') }}
                            <ScuHelpTooltip />
                        </v-col>
                        <v-col
                            cols="7"
                            class="px-3 py-0 text-body-1">
                            {{ scuHours?.toFixed(2) }}
                        </v-col>
                    </v-row>
                </template>
                <v-row class="text-caption">
                    <v-col
                        cols="5"
                        class="px-3 py-0 text-uppercase">
                        {{ service.size }}
                    </v-col>
                    <v-col
                        cols="7"
                        class="px-3 py-0">
                        {{ instanceCostPerHour }}
                    </v-col>
                </v-row>
                <v-row class="text-caption">
                    <v-col
                        cols="5"
                        class="px-3 py-0">
                        {{ $t('launchService.ssd-storage', [service.storage_volume.size]) }}
                    </v-col>
                    <v-col
                        cols="7"
                        class="px-3 py-0">
                        {{ ssdStorageCostPerHour }}
                    </v-col>
                </v-row>
                <v-row
                    v-if="iopsCostPerHour"
                    class="text-caption">
                    <v-col
                        cols="5"
                        class="px-3 py-0">
                        {{ $t('compute-iops', [service.storage_volume.iops]) }}
                    </v-col>
                    <v-col
                        cols="7"
                        class="px-3 py-0">
                        {{ iopsCostPerHour }}
                    </v-col>
                </v-row>
                <v-row
                    v-if="scaleFabricCostPerHour"
                    class="text-caption">
                    <v-col
                        cols="5"
                        class="px-3 py-0">
                        {{ $t('launchService.multiNodeInfra') }}
                    </v-col>
                    <v-col
                        cols="7"
                        class="px-3 py-0">
                        {{ scaleFabricCostPerHour }}
                    </v-col>
                </v-row>
                <v-row
                    v-if="secondaryEndpointCostPerHour"
                    class="text-caption">
                    <v-col
                        cols="5"
                        class="px-3 py-0">
                        {{ $t('dualEndpoints.secondaryEndpoint') }}
                    </v-col>
                    <v-col
                        cols="7"
                        class="px-3 py-0">
                        {{ secondaryEndpointCostPerHour }}
                    </v-col>
                </v-row>
                <v-row
                    v-if="maxScaleCostPerHour"
                    class="text-caption">
                    <v-col
                        cols="5"
                        class="px-3 py-0">
                        {{ maxScaleRedundancyLabel }}
                    </v-col>
                    <v-col
                        cols="7"
                        class="px-3 py-0">
                        {{ maxScaleCostPerHour }}
                    </v-col>
                </v-row>
                <v-row
                    v-if="gp3IopsCostPerHour"
                    class="text-caption">
                    <v-col
                        cols="5"
                        class="px-3 py-0">
                        {{ gp3IopsLabel }}
                    </v-col>
                    <v-col
                        cols="7"
                        class="px-3 py-0">
                        {{ gp3IopsCostPerHour }}
                    </v-col>
                </v-row>
                <v-row
                    v-if="gp3ThroughputCostPerHour"
                    class="text-caption">
                    <v-col
                        cols="5"
                        class="px-3 py-0">
                        {{ gp3ThroughputLabel }}
                    </v-col>
                    <v-col
                        cols="7"
                        class="px-3 py-0">
                        {{ gp3ThroughputCostPerHour }}
                    </v-col>
                </v-row>
                <v-row
                    v-if="!isServerless"
                    class="text-caption">
                    <v-col
                        cols="5"
                        class="px-3 py-0">
                        {{ $t('total') }}
                    </v-col>
                    <v-col
                        cols="7"
                        class="px-3 py-0">
                        {{ hourlyTotalCost }}
                    </v-col>
                </v-row>
            </div>
        </v-card-text>
    </v-card>
</template>

<script>
    import { mapGetters } from 'vuex'
    import { isServerlessTopology } from 'utils/service'
    import HelpToolTip from 'components/common/HelpTooltip.vue'
    import ScuHelpTooltip from 'components/common/ScuHelpTooltip.vue'

    export default {
        name: 'Spending',
        components: {
            HelpToolTip,
            ScuHelpTooltip,
        },
        props: {
            totalCost: {
                type: Number,
                default: 0,
            },
            scuHours: {
                type: Number,
                default: 0,
            },
            components: {
                type: Array,
                default: () => [],
            },
            service: {
                type: Object,
                default: () => { },
            },
            instanceCostPerHour: {
                type: String,
                default: '',
            },
            ssdStorageCostPerHour: {
                type: String,
                default: '',
            },
            iopsCostPerHour: {
                type: String,
                default: '',
            },
            scaleFabricCostPerHour: {
                type: String,
                default: '',
            },
            hourlyTotalCost: {
                type: String,
                default: '',
            },
            spendingDataUnAvailable: {
                type: Boolean,
                default: false,
            },
            maxScaleCostPerHour: {
                type: String,
                default: '',
            },
            maxScaleRedundancyLabel: {
                type: String,
                default: '',
            },
            secondaryEndpointCostPerHour: {
                type: String,
                default: '',
            },
            gp3IopsCostPerHour: {
                type: String,
                default: '',
            },
            gp3IopsLabel: {
                type: String,
                default: '',
            },
            gp3ThroughputCostPerHour: {
                type: String,
                default: '',
            },
            gp3ThroughputLabel: {
                type: String,
                default: '',
            },
            loading: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            ...mapGetters([ 'isFreeTrialActive' ]),
            isServerless() {
                return isServerlessTopology(this.service?.topology)
            },
            shouldShowDetailedServerlessSpending() {
                return !this.isFreeTrialActive &&
                    this.$unleash.isFeatureEnabled('enable-serverless-storage-scaling')
            },
        },
    }
</script>
